import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

interface NetworkStatusContextValue {
  /** Whether the network connection is currently unstable / server unreachable */
  isUnstable: boolean;
  /** Report a network-level error (failed fetch, timeout, 502, etc.) */
  reportNetworkError: () => void;
  /** Report a successful network request (resets the error counter) */
  reportNetworkSuccess: () => void;
}

const NetworkStatusContext = createContext<NetworkStatusContextValue>({
  isUnstable: false,
  reportNetworkError: () => {},
  reportNetworkSuccess: () => {}
});

/** Threshold of errors within the time window to trigger the unstable state */
const ERROR_THRESHOLD = 3;
/** Time window in ms — errors older than this are discarded */
const ERROR_WINDOW_MS = 30_000;
/** How long after the last error (with no success) before auto-recovering */
const RECOVERY_DELAY_MS = 15_000;

// ---------------------------------------------------------------------------
// Global listener so non-React code (http.tsx) can report network events
// ---------------------------------------------------------------------------
type NetworkEventListener = {
  onError: () => void;
  onSuccess: () => void;
  isUnstable: () => boolean;
};

let globalListener: NetworkEventListener | null = null;

/** Called from http.tsx errorHandler when a network / server error is detected */
export function reportGlobalNetworkError() {
  globalListener?.onError();
}

/** Called from http.tsx response interceptor on successful responses */
export function reportGlobalNetworkSuccess() {
  globalListener?.onSuccess();
}

/** Called from http.tsx to check if the connection banner is already visible */
export function isGlobalNetworkUnstable(): boolean {
  return globalListener?.isUnstable() ?? false;
}

export const NetworkStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isUnstable, setIsUnstable] = useState(false);
  const errorTimestampsRef = useRef<number[]>([]);
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRecoveryTimer = useCallback(() => {
    if (recoveryTimerRef.current) {
      clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
  }, []);

  const startRecoveryTimer = useCallback(() => {
    clearRecoveryTimer();
    recoveryTimerRef.current = setTimeout(() => {
      setIsUnstable(false);
      errorTimestampsRef.current = [];
    }, RECOVERY_DELAY_MS);
  }, [clearRecoveryTimer]);

  // Browser online/offline
  useEffect(() => {
    const handleOffline = () => setIsUnstable(true);
    const handleOnline = () => startRecoveryTimer();

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [startRecoveryTimer]);

  const reportNetworkError = useCallback(() => {
    const now = Date.now();
    errorTimestampsRef.current = errorTimestampsRef.current.filter(
      t => now - t < ERROR_WINDOW_MS
    );
    errorTimestampsRef.current.push(now);

    if (errorTimestampsRef.current.length >= ERROR_THRESHOLD) {
      setIsUnstable(true);
      startRecoveryTimer();
    }
  }, [startRecoveryTimer]);

  const reportNetworkSuccess = useCallback(() => {
    if (errorTimestampsRef.current.length > 0) {
      errorTimestampsRef.current = [];
    }
    clearRecoveryTimer();
    setIsUnstable(false);
  }, [clearRecoveryTimer]);

  // Keep a ref so the global listener always reads the latest isUnstable
  const isUnstableRef = useRef(isUnstable);
  isUnstableRef.current = isUnstable;

  // Register as the global listener for non-React code
  useEffect(() => {
    globalListener = {
      onError: reportNetworkError,
      onSuccess: reportNetworkSuccess,
      isUnstable: () => isUnstableRef.current,
    };
    return () => {
      globalListener = null;
    };
  }, [reportNetworkError, reportNetworkSuccess]);

  // Cleanup
  useEffect(() => {
    return () => clearRecoveryTimer();
  }, [clearRecoveryTimer]);

  return (
    <NetworkStatusContext.Provider value={{ isUnstable, reportNetworkError, reportNetworkSuccess }}>
      {children}
    </NetworkStatusContext.Provider>
  );
};

export function useNetworkStatus() {
  return useContext(NetworkStatusContext);
}

export default NetworkStatusContext;
